import { useCallback, useEffect, useMemo, useState } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { FormHelperText, Typography } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import {
  useUploadDocument,
  type BatchUploadSuccessData,
} from "@features/vessel-nomination/add/hooks/useUploadDocument";
import { generateAdditionalDocumentField } from "@features/vessel-nomination/utils";
import { t } from "i18next";
import { useFieldArray } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { FORM_FIELDS, type VesselNominationOutletContext } from "../../constants";

const emptyArray: never[] = [];
const LANG_BASE = "vessel-nomination";
const fieldName = FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS;
const MAX_FILES = 25;

const getAdditionalDocumentKey = (file: UploadEntry, idx: number) => {
  const entry = file as Record<string, unknown>;
  const url = typeof entry.url === "string" && entry.url.trim() ? entry.url : undefined;

  if (url) return url;

  const name = typeof entry.name === "string" && entry.name.trim() ? entry.name : undefined;
  const lastModified = typeof entry.lastModified === "number" ? entry.lastModified : undefined;
  const size = typeof entry.size === "number" ? entry.size : undefined;
  const type = typeof entry.type === "string" && entry.type.trim() ? entry.type : undefined;
  const signature = [name, size, type, lastModified]
    .filter((value) => value !== undefined)
    .join("|");

  return signature || `${fieldName}-${idx}`;
};

const documentTypeOptions = [
  { label: "Certificate", value: "CERTIFICATE" },
  { label: "Permit", value: "PERMIT" },
  { label: "Document", value: "DOCUMENT" },
  { label: "Insurance", value: "INSURANCE" },
  { label: "Vessel Master-SoF", value: "VESSEL_MASTER_SOF" },
  { label: "Others-Specify", value: "OTHERS_SPECIFY" },
];

const AdditionalDocumentsCard = () => {
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { notify } = useNotification();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();
  const [error, setError] = useState("");

  const additionalDocumentFiles = useUploadStore(
    (state) => state.filesByKey[fieldName] ?? emptyArray,
  );

  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const removeFileAtIndex = useUploadStore((state) => state.removeFileAtIndex);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);
  const handleFieldChange = (key: string, value: unknown) => {
    formData.setValue(
      key as Parameters<typeof formData.setValue>[0],
      value as Parameters<typeof formData.setValue>[1],
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      },
    );

    updateFormData(
      key as Parameters<typeof updateFormData>[0],
      value as Parameters<typeof updateFormData>[1],
    );
  };
  const { remove } = useFieldArray({
    control: formData.control,
    name: FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS,
  });

  useEffect(() => {
    const uploadStore = useUploadStore.getState();
    if (uploadStore.getFilesForKey(fieldName).length) return;

    const storedDocuments = formData.getValues(fieldName);
    if (!Array.isArray(storedDocuments) || !storedDocuments.length) return;

    const normalizedEntries = storedDocuments.map((doc, idx) => {
      const typedDoc = (doc ?? {}) as Record<string, unknown>;
      const documentUrl = typeof typedDoc.documentUrl === "string" ? typedDoc.documentUrl : "";
      const fallbackFromUrl =
        documentUrl && documentUrl.includes("/") ? documentUrl.split("/").pop() : documentUrl;
      const resolvedName = fallbackFromUrl || `document-${idx + 1}`;

      return {
        name: resolvedName,
        url: documentUrl,
      };
    });

    uploadStore.setFilesForKey(fieldName, normalizedEntries);
  }, [formData]);

  const handleUploadSuccess = ({ documents, startIdx }: BatchUploadSuccessData) => {
    const currentDocuments = [...(formData.getValues(fieldName) ?? [])];

    updateFilesForKey(fieldName, (prev) => {
      const nextEntries = [...prev];

      documents.forEach(({ documentUrl, documentName }, fileOffset) => {
        const at = startIdx + fileOffset;
        const fileName = documentName || `document-${at + 1}`;

        nextEntries[at] = {
          name: fileName,
          url: documentUrl,
        };

        currentDocuments[at] = {
          documentUrl,
          documentType: "",
          documentName,
          otherSpecify: "",
          issueDate: null,
          expiryDate: null,
        };
      });

      return nextEntries;
    });

    formData.setValue(fieldName, currentDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    updateFormData(fieldName, currentDocuments);
  };

  const handleUploadError = (error: Error, meta: { startIdx?: number; count: number }): void => {
    notify({
      title: t(`${LANG_BASE}.notifications.additional-documents-upload-error-title`),
      description: t(`${LANG_BASE}.notifications.additional-documents-upload-error-description`),
      type: "error",
    });

    console.error("Error on upload documents:", error);

    const startIdx = meta.startIdx ?? 0;
    const { count } = meta;

    const toRemoveDescending = Array.from({ length: count }, (_, i) => startIdx + i).reverse();

    toRemoveDescending.forEach((idx) => {
      removeFileAtIndex(fieldName, idx);
      handleRemoveFile(idx);
    });
  };

  const uploadAdditionalDocuments = useUploadDocument({
    multiple: true,
    handleUploadSuccess,
    handleUploadError,
  });
  const handleUpload = (incomingFiles: File[]) => {
    setError("");
    if (!incomingFiles?.length) return;

    uploadAdditionalDocuments.mutate({
      files: incomingFiles,
      documentType: "ADDITIONAL_DOCUMENT",
      startIdx: formData.getValues(fieldName)?.length ?? 0,
    });
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");

    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    );
  };

  const handleRemoveFile = (idx: number) => {
    const currentDocuments = [...(formData.getValues(fieldName) ?? [])];
    const nextDocuments = currentDocuments.filter((_value: unknown, i: number) => i !== idx);

    remove(idx);

    formData.setValue(fieldName, nextDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(fieldName, nextDocuments);
  };

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount = useUploadStore.getState().getFilesForKey(fieldName).length ?? 0;

      if (existingFilesCount + incomingFiles.length > MAX_FILES) {
        return t("form-validation.max-files", { max: MAX_FILES });
      }

      return null;
    },
    [t],
  );

  const handleValidationResult = useCallback((message: string | null) => {
    setError(message ?? "");
  }, []);

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const isMaxFilesReached = additionalDocumentFiles.length >= MAX_FILES;

  return (
    <div key={fieldName} className="rounded-sm border border-[#E6EDF2] relative">
      {uploadAdditionalDocuments.isPending && (
        <div className="absolute inset-0 flex justify-center pointer-events-none top-28 opacity-100">
          <RotateRightIcon
            fontSize="large"
            sx={{
              "animation": "mui-spin 0.9s linear infinite",
              "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
            }}
          />
        </div>
      )}

      <div className="flex items-center bg-[#F7F8FA] px-8 min-h-12">
        <Typography variant="body2">
          {t(`${LANG_BASE}.add.form.sections.additional-documents`)}
        </Typography>
      </div>

      <div className="flex py-4 px-8 gap-4 flex-col">
        <div className={uploadAdditionalDocuments.isPending ? "opacity-50" : ""}>
          <FileUpload
            testId={`${fieldName}-upload`}
            onUpload={handleUpload}
            onReject={handleReject}
            name={fieldName}
            conditions={uploadConditions}
            files={additionalDocumentFiles}
            setFiles={(next) => setFilesForKey(fieldName, next.slice(0, MAX_FILES))}
            maxFiles={MAX_FILES}
            allowedExtensions={allowedExtensions}
            disabled={uploadAdditionalDocuments.isPending || isMaxFilesReached}
            uploadDescription={t("upload.additional-documents-upload", {
              extensions: uploadConditions.format.join(", "),
              size: uploadConditions.maxSizeLabel,
            })}
            validateDrop={(selectedCount) => {
              if (selectedCount > MAX_FILES) {
                return t("form-validation.max-files", { max: MAX_FILES });
              }
              return null;
            }}
            validateFiles={validateUploadFiles}
            onValidationResult={handleValidationResult}
          />

          {error && (
            <FormHelperText
              sx={{
                ml: 0,
                fontWeight: 500,
                fontSize: 12,
                marginY: "4px",
                height: "20px",
                color: "error.main",
              }}
            >
              {error}
            </FormHelperText>
          )}
        </div>

        <div>
          {additionalDocumentFiles.map((file, idx) => {
            const currentDocument = formData.getValues(fieldName)?.[idx];
            const documentKey = getAdditionalDocumentKey(file, idx);
            const hasDocumentUrl = !!currentDocument?.documentUrl;
            const showOtherDocumentType = currentDocument?.documentType === "OTHERS_SPECIFY";

            return (
              <div
                className={`grid w-full gap-4 grid-cols-1 ${
                  showOtherDocumentType ? "lg:grid-cols-5" : "lg:grid-cols-4"
                }`}
                key={documentKey}
              >
                <div key={`${documentKey}-file`}>
                  <SingleFile
                    testId={`${fieldName}-${idx}`}
                    file={file}
                    fieldName={fieldName}
                    currentFileIndex={idx}
                    setFiles={() => handleRemoveFile(idx)}
                    isLoading={
                      uploadAdditionalDocuments.isPending &&
                      !formData.getValues(fieldName)?.[idx]?.documentUrl
                    }
                  />
                </div>

                {hasDocumentUrl && (
                  <>
                    <div key={`${documentKey}-document-type`}>
                      <FormItem
                        testId={`additional-documents-${idx}-document-type`}
                        name={generateAdditionalDocumentField.documentType(idx)}
                        type="select"
                        label={t(`${LANG_BASE}.add.form.fields.document-type`)}
                        options={documentTypeOptions}
                        onFieldChange={handleFieldChange}
                        required
                      />
                    </div>

                    {showOtherDocumentType && (
                      <div key={`${documentKey}-other-document-type`}>
                        <FormItem
                          testId={`additional-documents-${idx}-other-document-type`}
                          name={generateAdditionalDocumentField.otherSpecify(idx)}
                          type="text"
                          maxChars={30}
                          label={t(`${LANG_BASE}.add.form.fields.other-specify`)}
                          onFieldChange={handleFieldChange}
                          required
                        />
                      </div>
                    )}

                    <div key={`${documentKey}-issue-date`}>
                      <FormItem
                        testId={`additional-documents-${idx}-issue-date`}
                        name={generateAdditionalDocumentField.issueDate(idx)}
                        type="date-picker"
                        label={t(`${LANG_BASE}.add.form.fields.issue-date`)}
                        onFieldChange={handleFieldChange}
                        timePicker={false}
                        dateConstraint="not-future"
                        required
                      />
                    </div>

                    <div key={`${documentKey}-expiry-date`}>
                      <FormItem
                        testId={`additional-documents-${idx}-expiry-date`}
                        name={generateAdditionalDocumentField.expiryDate(idx)}
                        type="date-picker"
                        label={t(`${LANG_BASE}.add.form.fields.expiration-date`)}
                        onFieldChange={handleFieldChange}
                        timePicker={false}
                        dateConstraint="not-past"
                        required
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdditionalDocumentsCard;
